import { useEffect, useState, useRef, MouseEvent, ReactNode } from "react";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { useLocation, useNavigate } from "react-router";
import {
  FilterOption,
  HandleSelect,
  isValidEntityType,
  MetadataType,
} from "types";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { connect } from "react-redux";
import { State } from "redux/store";
import classNames from "classnames";
import {
  getAllSelectedFilters,
  getEntityTypeFilter,
  getTemporaryExportDataArray,
  getFetchError,
  getLastSelectedIndex,
  getScrollApplied,
  getExportRequestData,
  getTemporaryAssignedTags,
  getDataPageScrollPosition,
  getClusterPageScrollPosition,
  getSimilarityVertexCluster,
  getNoResultsFound,
  getCurrentFilterQueries,
  getShowQueryPanel,
  getTempExportCount,
  getDatasetContextualLabels,
  getUserTags,
  getSearchOptions,
  getIsSearchByRelevanceEnabled,
  getRelevanceScores,
  getIsAssetPreviewEnabled,
  getIsCardslistLoading,
  getIsNewFiltersPanel,
  getIsShowDebugInfoEnabled,
  getNavigationClusterLoader,
  getGlobalMDType,
} from "redux/SingleDataset/selectors";

import {
  clearFilters,
  fetchDataset,
  fetchUserTags,
  setLastSelectedIndex,
  setScrollApplied,
  setTemporaryAssignedTags,
  updateTemporaryExportData,
  fetchTemporaryAssignedTags,
  fetchDatasetContextualLabels,
  addAllForTempExport,
  addAllMediaIDsFromClusterPageToTemp,
  clearSelectedMedia,
  setGlobalMDType,
} from "redux/SingleDataset/actions";
import {
  useWindowResize,
  calculatePlaceHolderCount,
} from "helpers/utility/utilities";
import {
  PAGE_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_ID,
  VERTEX_TYPE,
  ENTITY_TYPE_PARAMETER,
  GRANULARITY_PARAMETER,
  LABELS_PARAMETER,
  USER_TAGS_PARAMETER,
  SEARCH_TEXT_PARAMETER,
} from "helpers/constants/miscellaneous";
import FetchErrorCard from "views/components/FetchErrorCard";
import {
  NoMatchFoundCard,
  NoMatchFoundPlaceholder,
} from "views/components/NoMatchFoundCard";
import { AMP_DATASET_EXPLORATION_EVENT__FILTER__ALL_CLEARED } from "helpers/constants/amplitudeEvents";
import { AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE } from "helpers/constants/amplitudeProperties";

import FloatingActions from "views/components/FloatingActions";
import QueryPanel from "views/components/QueryPanel";
import EnrichmentCard from "./EnrichmentCard";
import VertexCard from "./VertexCard";

import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import useQueryParam from "hooks/useQueryParam";
import useVqlParams from "hooks/useVqlParams";
import FloatingPanel from "views/components/FloatingPanel";
import { Option, SelectListProps } from "views/uikit/FormFields/SelectList";
import { VLButtonProps } from "views/uikit/Buttons/VLButton";
import { RightPanelMode } from "..";

interface LabelPropagationProps {
  onLabelClick: (value: string[]) => void;
  options?: Option[];
  displayNewFloatingPanel?: boolean;
  isLabelPropagationEnabled: boolean;
}

interface DataListProps {
  isContentLoading: boolean;
  navigationCluster: any;
  navigationClusterTags: any;
  page: any;
  showMetadataSummary: boolean;
  fetchError: Error | null;
  isVertexSelected: boolean;
  exportDataArray: any[];
  lastSelectedIndex: any;
  dataPageScrollPosition: number;
  clusterPageScrollPosition: number;
  scrollApplied: boolean;
  noResultsFound: boolean;
  clusters: Cluster[];
  labelPropagation: LabelPropagationProps;
  isDataPage: boolean;
  rightPanelMode: RightPanelMode;
  title?: { text: string; icon?: ReactNode };
}

export interface Cluster {
  id: string;
  label: string;
  title: string;
  cluster_id: string;
  previews: Preview[];
  onDetailsClick?: (data: Cluster) => void;
  floatingDetails?: string;
  clusterLabel?: string;
}

export type PreviewBorderColor = "green" | "red";

export interface ActionItem {
  action: VLButtonProps;
  subAction?: SelectListProps;
  tooltip?: string; // tooltip text to display on hover
}

export interface Preview {
  image_id: string;
  media_id: string;
  type: "IMAGE" | "OBJECT";
  video_uri?: string;
  title?: string;
  actionList?: ActionItem[];
  borderColor?: PreviewBorderColor;
  opacity: number;
  media_uri: string | null;
  relevanceType: string | null;
  floatingDetails?: string;
  label?: string;
  displayIgnorLayout?: boolean;
}

const DataList = ({
  isContentLoading,
  navigationCluster,
  navigationClusterTags,
  page,
  showMetadataSummary,
  fetchError,
  isVertexSelected,
  exportDataArray,
  lastSelectedIndex,
  dataPageScrollPosition,
  clusterPageScrollPosition,
  scrollApplied,
  noResultsFound,
  labelPropagation,
  clusters,
  isDataPage,
  rightPanelMode,
  title,
}: DataListProps) => {
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [indexOfPointerOut, setIndexOfPointerOut] = useState(-1);
  const globalMDType = useSelector(getGlobalMDType);

  const dispatch = useDispatch();
  const { vqlQueries } = useVqlParams();
  const queryParamsFilters = useAllQueryParamsFilters();

  const { datasetId, clusterId } = useParams();
  const { height, width } = useWindowResize();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const entityTypeFromQueryParam = isValidEntityType(
    queryParamsFilters[ENTITY_TYPE_PARAMETER]
  )
    ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
    : null;
  const labelFromQueryParam = useMemo(
    () => JSON.parse(queryParamsFilters[LABELS_PARAMETER] || "[]"),
    [queryParamsFilters]
  );
  const userTagsFromQueryParam = useMemo(
    () => JSON.parse(queryParamsFilters[USER_TAGS_PARAMETER] || "[]"),
    [queryParamsFilters]
  );
  const searchTextParam = queryParamsFilters[SEARCH_TEXT_PARAMETER] || null;

  const currentFilterQueries = useSelector((state) =>
    getCurrentFilterQueries(state, queryParamsFilters, false, false)
  );
  const entityTypeFilter = useSelector((state) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam)
  );
  const allSelectedFilters = useSelector((state) =>
    getAllSelectedFilters(state, queryParamsFilters)
  );
  const showQueryPanel = useSelector((state) =>
    getShowQueryPanel(state, vqlQueries)
  );
  const exportRequestData = useSelector((state) =>
    getExportRequestData(state, queryParamsFilters, true)
  );
  const datasetLabels = useSelector((state) =>
    getDatasetContextualLabels(state, labelFromQueryParam)
  );
  const datasetUserTags = useSelector((state: State) =>
    getUserTags(state, userTagsFromQueryParam)
  );
  const rawRelevanceScores = useSelector((state: any) =>
    getRelevanceScores(state, entityTypeFromQueryParam)
  );

  const searchOptions = useSelector((state) =>
    getSearchOptions(state, entityTypeFromQueryParam)
  );
  const isSearchByRelevanceEnabled = useSelector((state) =>
    getIsSearchByRelevanceEnabled(state, entityTypeFromQueryParam)
  );
  const isNewFiltersPanel = useSelector(getIsNewFiltersPanel);
  const exportData = useSelector(getTemporaryExportDataArray);
  const showDebugInfo = useSelector(getIsShowDebugInfoEnabled);
  const isCardslistLoading = useSelector(getIsCardslistLoading);
  const isAssetPreviewEnabled = useSelector(getIsAssetPreviewEnabled);
  const isNavigationClusterLoading = useSelector(getNavigationClusterLoader);

  const assignedTags = useSelector(getTemporaryAssignedTags);
  const exportCount = useSelector(getTempExportCount);
  const showFloatingActions = exportCount > 0;

  const cardContainer = useRef<HTMLDivElement>(null);
  const cardElement = useRef<HTMLDivElement>(null);

  const isClusterPage = !!datasetId && !!clusterId;
  const showSelectOption = isDataPage || isClusterPage;

  let sortedList: any[];
  let clusterTagsByClusterId: { [key: string]: any[] } = {};
  if (fetchError) {
    sortedList = [];
    clusterTagsByClusterId = {};
  }
  if (isDataPage) {
    sortedList = clusters;
    if (clusters?.length) {
      clusterTagsByClusterId = clusters.reduce((acc: any, cluster: any) => {
        acc[cluster.cluster_id] = cluster;
        return acc;
      }, {});
    } else {
      clusterTagsByClusterId = {};
    }
  } else {
    sortedList = [];
    clusterTagsByClusterId = {};
    if (navigationCluster) {
      sortedList.push(...navigationCluster.previews);
      clusterTagsByClusterId = navigationClusterTags?.previews
        ? navigationClusterTags.previews.reduce((acc: any, preview: any) => {
            acc[preview.media_id] = preview;
            return acc;
          }, {})
        : {};
    }
  }

  let length = isContentLoading
    ? 1
    : isVertexSelected
      ? sortedList.length + 1
      : sortedList.length;
  const showPlaceholders =
    !isVertexSelected && (isContentLoading || sortedList.length === 0);

  const pageNumber = useQueryParam(PAGE_PARAMETER);
  const lastPosition = isDataPage
    ? dataPageScrollPosition
    : clusterPageScrollPosition;

  const showRelevance =
    searchOptions.semanticSearch &&
    isSearchByRelevanceEnabled &&
    searchTextParam;
  const relevanceScores = useMemo(
    () => (showRelevance ? rawRelevanceScores : null),
    [showRelevance, rawRelevanceScores]
  );

  const listClassNames = classNames(styles.list, {
    [styles.metadatasummaryList]: rightPanelMode === "summary",
    [styles.propagationList]: rightPanelMode === "labels",
    [styles.hiddenOverflowY]: length <= 0,
  });

  const removeParameters = (searchParams: URLSearchParams) => {
    let keysToBeDeleted: any = [];
    searchParams.forEach((value: any, key: any) => {
      //ignoring the filters that are not reset
      if (
        [PAGE_PARAMETER, GRANULARITY_PARAMETER, ENTITY_TYPE_PARAMETER].includes(
          key
        )
      ) {
        //do nothing
      } else {
        keysToBeDeleted.push(key);
      }
    });

    if (keysToBeDeleted.length > 0) {
      keysToBeDeleted.forEach((key: any) => searchParams.delete(key));
    }
    return searchParams;
  };

  const onClearFilters = async () => {
    dispatch(clearFilters() as any);

    const selectedEntities = entityTypeFilter
      .filter((entity: FilterOption) => entity.selected)
      .map((entity: FilterOption) => entity.text);
    let value;
    if (selectedEntities.length > 0) {
      value = selectedEntities.join(",");
    } else {
      value = "Images";
    }
    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__FILTER__ALL_CLEARED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE]: value,
    });
    if (!isVertexSelected) {
      searchParams.set(PAGE_PARAMETER, "1");
    }
    const updatedSearchParams = removeParameters(searchParams);
    const newParams = `?${updatedSearchParams.toString()}`;
    let newPath = location.pathname;

    if (newPath.includes("/cluster")) {
      newPath = newPath.split("/cluster")[0];
    }
    dispatch(
      fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any
    );
    dispatch(fetchUserTags(datasetId, queryParamsFilters) as any);
    dispatch(fetchDataset(datasetId) as any);
    navigate(newPath.concat(newParams));
  };

  const showNoMatchFound = () => {
    if (fetchError) {
      return <FetchErrorCard />;
    } else if (!isContentLoading && length === 0 && !isVertexSelected) {
      return <NoMatchFoundCard handleClearFilters={onClearFilters} />;
    }
  };

  const showNoMatchFoundPlaceholder =
    !isContentLoading && isVertexSelected && length === 1 && noResultsFound;

  const handleSetVertex = useCallback(
    (id: string, type: "MEDIA" | "CLUSTER") => {
      searchParams.set(PAGE_PARAMETER, "1");
      if (type === "MEDIA") {
        searchParams.set(VERTEX_ID, id);
        searchParams.set(VERTEX_TYPE, "MEDIA");
      } else if (type === "CLUSTER") {
        const vertexCluster = clusters?.find(
          (cluster: Cluster) => cluster.cluster_id === id
        );
        if (!!vertexCluster) {
          searchParams.set(VERTEX_CONTEXT, currentFilterQueries);
          searchParams.set(VERTEX_ID, vertexCluster.previews[0].media_id);
          searchParams.set(VERTEX_TYPE, "CLUSTER");
        }
      }
      const path = isDataPage
        ? `/dataset/${datasetId}/data`
        : `/dataset/${datasetId}/data/cluster/${clusterId}`;
      navigate({
        pathname: path,
        search: `?${searchParams.toString()}`,
      });
    },
    //eslint-disable-next-line
    [dispatch, datasetId, navigate, searchParams, pageNumber, clusters]
  );

  const updateExportDataForSingleItem = (
    singleData: any,
    clusterID: string
  ) => {
    const selectedExportData = isDataPage
      ? singleData
      : { ...singleData, cluster_id: clusterID };
    const updateType = isDataPage ? "cluster" : "media";

    dispatch(
      updateTemporaryExportData(
        { ...selectedExportData, cluster_filters: allSelectedFilters },
        updateType
      ) as any
    );
  };

  const getSelectedExportItemIDs = (
    exportDataArray: any[],
    clusterId: string,
    isDataPage: boolean
  ): any[] => {
    // check if the whole cluster is selected for MediaIDs
    const clusterForMediaIDs = exportDataArray.find(
      (item: any) =>
        item.cluster_id === clusterId && item.exportType === "cluster"
    );
    const selectedCluster = clusterForMediaIDs ? navigationCluster : undefined;

    // If data page, return all cluster IDs
    if (isDataPage) {
      return exportDataArray.map((item: any) => item.cluster_id);
    }

    // If a selectedCluster exists, get only the selected previews
    if (selectedCluster) {
      return selectedCluster.previews
        .filter(
          (preview: any) =>
            !clusterForMediaIDs.excludeMediaIDs ||
            !clusterForMediaIDs.excludeMediaIDs.includes(preview.media_id)
        )
        .map((preview: any) => preview.media_id);
    }

    // Otherwise, return media IDs matchimg the specific cluster ID
    return exportDataArray
      .filter(
        (item: any) =>
          item.exportType === "media" && item.cluster_id === clusterId
      )
      .map((item: any) => item.media_id);
  };

  const getTempArray = (
    sortedList: any[],
    lastIndex: string,
    currentIndex: string
  ): any[] => {
    const lastIdx = lastIndex ? Number(lastIndex.split("index-")[1]) : 0;
    const currIdx = Number(currentIndex.split("index-")[1]);

    const isForward = currIdx > lastIdx;
    return isForward
      ? sortedList.slice(lastIdx, currIdx + 1)
      : sortedList.slice(currIdx, lastIdx + 1);
  };

  const handleSelect: HandleSelect = (
    e: MouseEvent,
    selected: boolean,
    specificIndex: string,
    clusterID: string,
    singleData: any
  ) => {
    if (!e.shiftKey) {
      updateExportDataForSingleItem(singleData, clusterID);
      dispatch(setLastSelectedIndex(specificIndex));
      return;
    }

    const selectedExportItemIDs = new Set(
      getSelectedExportItemIDs(exportDataArray, clusterId!, isDataPage)
    );

    // Fetch the temporary array
    const tempArray = getTempArray(
      sortedList,
      lastSelectedIndex,
      specificIndex
    );

    tempArray.forEach((item: any) => {
      const dynamicID = isDataPage ? item.cluster_id : item.media_id;
      const isItemSelected = selectedExportItemIDs.has(dynamicID);
      const dynamicClusterID = isDataPage
        ? item.cluster_id
        : navigationCluster.cluster_id;

      if ((selected && isItemSelected) || (!selected && !isItemSelected)) {
        updateExportDataForSingleItem(item, dynamicClusterID);
      }
    });

    dispatch(setLastSelectedIndex(specificIndex));
  };

  useEffect(() => {
    //clear  whenever page switches
    dispatch(setLastSelectedIndex(null));

    //eslint-disable-next-line
  }, [clusterId, datasetId]);

  useEffect(() => {
    cardContainer.current && cardContainer.current.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const extraWidth = 26;
    setPlaceholderCount(
      calculatePlaceHolderCount(
        cardContainer?.current,
        cardElement?.current,
        length,
        extraWidth
      )
    );
  }, [height, width, length, cardElement, isVertexSelected, isContentLoading]);

  const scrollToOption = (index: number) => {
    const container = cardContainer.current;
    if (container) {
      const element = container?.children[index] as HTMLElement;
      if (element) {
        container.scroll({ top: element.offsetTop - 14 });
        let lastScrollTop = container.scrollTop;

        const onScroll = () => {
          const currentScrollTop = container?.scrollTop;

          if (currentScrollTop === lastScrollTop) {
            dispatch(setScrollApplied(true));
            container?.removeEventListener("scroll", onScroll);
          } else {
            lastScrollTop = currentScrollTop!;
          }
        };
        container.addEventListener("scroll", onScroll);
      }
    }
  };

  useEffect(() => {
    if (!scrollApplied) {
      if (isDataPage && sortedList) {
        scrollToOption(lastPosition);
      } else if (isClusterPage && navigationCluster) {
        scrollToOption(lastPosition);
      }
    }
    //eslint-disable-next-line
  }, [isDataPage, isClusterPage, navigationCluster, sortedList, scrollApplied]);

  useEffect(() => {
    const selectedClusterOnDataPage =
      isClusterPage &&
      exportDataArray.find(
        (singleData: any) =>
          singleData.cluster_id === clusterId &&
          singleData.exportType === "cluster"
      );

    const handleSelectAll = () => {
      if (isClusterPage) {
        if (!!selectedClusterOnDataPage) {
          //will remove the selected cluster from cart and add medias
          dispatch(
            updateTemporaryExportData(
              {
                ...selectedClusterOnDataPage,
                cluster_filters: allSelectedFilters,
              },
              "cluster"
            ) as any
          );
        }
        dispatch(clearSelectedMedia() as any);
        dispatch(
          addAllMediaIDsFromClusterPageToTemp(
            datasetId,
            allSelectedFilters
          ) as any
        );
      } else if (!!datasetId) {
        dispatch(addAllForTempExport(clusters, allSelectedFilters) as any);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      const isTyping =
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          (active as HTMLElement).isContentEditable);

      if (isTyping) return;

      if ((e.ctrlKey || e.metaKey) && e.key === "a") {
        e.preventDefault();
        handleSelectAll();
      }
    };

    if (!isContentLoading && sortedList.length > 0) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line
  }, [isContentLoading, sortedList]);

  useEffect(() => {
    if (exportDataArray.length > 0) {
      dispatch(
        fetchTemporaryAssignedTags(datasetId!, exportRequestData) as any
      );
    } else if (assignedTags.length > 0) {
      dispatch(setTemporaryAssignedTags([]));
    }
    //eslint-disable-next-line
  }, [exportDataArray]);

  const handleGlobalMDTypeChange = (type: MetadataType) => {
    if (type === globalMDType) return;
    dispatch(setGlobalMDType(type));
  };

  return (
    <div className={styles.singleListWrapper} data-test-id="singleListWrapper">
      {showQueryPanel && <QueryPanel />}
      {showFloatingActions && !labelPropagation.displayNewFloatingPanel && (
        <FloatingActions />
      )}
      {showFloatingActions &&
        labelPropagation.displayNewFloatingPanel &&
        labelPropagation?.options &&
        labelPropagation.options.length > 0 && (
          <FloatingPanel
            onOptionSelect={(value: string[]) => {
              labelPropagation.onLabelClick(value);
            }}
            options={labelPropagation?.options}
          />
        )}
      {title && (
        <div className={styles.title}>
          <span className={styles.titleText}>{title.text}</span>
          {title.icon || <noscript />}
        </div>
      )}
      <div
        className={listClassNames}
        id="data-page-cards-list"
        ref={cardContainer}
      >
        <VertexCard
          index={-2}
          globalMDType={globalMDType}
          cardElementRef={cardElement}
          cardContainerRef={cardContainer}
          indexOfPointerOut={indexOfPointerOut}
          showMetadataSummary={showMetadataSummary}
          //
          clusters={clusters}
          exportData={exportData}
          showDebugInfo={showDebugInfo}
          datasetLabels={datasetLabels}
          datasetUserTags={datasetUserTags}
          relevanceScores={relevanceScores}
          isNewFiltersPanel={isNewFiltersPanel}
          isCardslistLoading={isCardslistLoading}
          isAssetPreviewEnabled={isAssetPreviewEnabled}
          isNavigationClusterLoading={isNavigationClusterLoading}
          //

          onSelect={handleSelect}
          onIndexOfPointerOut={setIndexOfPointerOut}
          onGlobalMDTypeChange={handleGlobalMDTypeChange}
        />

        {sortedList?.map((singleData: any, index: number) => {
          const clusterID = isDataPage
            ? singleData.cluster_id
            : navigationCluster?.cluster_id;

          let userTagsData = isDataPage
            ? (clusterTagsByClusterId[singleData.cluster_id] as any)
                ?.user_tags || ({} as any)
            : (clusterTagsByClusterId[singleData.media_id] as any)?.user_tags ||
              [];

          return (
            <EnrichmentCard
              page={page}
              key={index}
              index={index}
              isVertex={false}
              datasetId={datasetId!}
              singleData={singleData}
              clusterID={clusterID}
              isDataPage={isDataPage}
              globalMDType={globalMDType}
              cardElementRef={cardElement}
              containerRef={cardContainer}
              isClusterPage={isClusterPage}
              userTagsData={userTagsData || []}
              showSelectOption={showSelectOption}
              indexOfPointerOut={indexOfPointerOut}
              showMetadataSummary={showMetadataSummary}
              //
              clusters={clusters}
              exportData={exportData}
              showDebugInfo={showDebugInfo}
              datasetLabels={datasetLabels}
              datasetUserTags={datasetUserTags}
              relevanceScores={relevanceScores}
              isNewFiltersPanel={isNewFiltersPanel}
              isCardslistLoading={isCardslistLoading}
              isAssetPreviewEnabled={
                title?.text !== "Review" && isAssetPreviewEnabled
              }
              isNavigationClusterLoading={isNavigationClusterLoading}
              //
              onSelect={handleSelect}
              onSetVertex={handleSetVertex}
              onIndexOfPointerOut={setIndexOfPointerOut}
              onGlobalMDTypeChange={handleGlobalMDTypeChange}
              cardTitle={singleData.title}
            />
          );
        })}
        {showPlaceholders &&
          [...Array(placeholderCount)].map((_, index) => (
            <div key={index} className={styles.cardPlaceHolder}>
              {showNoMatchFoundPlaceholder && index === 0 && (
                <NoMatchFoundPlaceholder handleClearFilters={onClearFilters} />
              )}
            </div>
          ))}
        {showNoMatchFound()}
      </div>
    </div>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    noResultsFound: getNoResultsFound(state),
    fetchError: getFetchError(state),
    isVertexSelected: !!getSimilarityVertexCluster(state),
    exportDataArray: getTemporaryExportDataArray(state),
    lastSelectedIndex: getLastSelectedIndex(state),
    scrollApplied: getScrollApplied(state),
    dataPageScrollPosition: getDataPageScrollPosition(state),
    clusterPageScrollPosition: getClusterPageScrollPosition(state),
  };
};

export default connect(mapStatesToProps)(DataList);
